import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { clearStaffSessionEnd, markStaffSessionEnded, onStaffSessionEnded, staffSignedIn } from '@/services/sessionEnd'
import { endImpersonation } from '../services/adminAuth'
import { getImpersonation } from './impersonation'

export interface User {
  id: number
  email: string
  role: string
  name: string | null
  platform_role: 'super_admin' | null
  leagues: Array<{ league_id: number; role: 'admin' | 'editor' | 'stat-keeper' | 'captain' }>
  /** The leagues where the account captains a team (registration step 17's login / accept / claim). */
  captain_leagues?: number[]
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  login: (token: string, user: User) => void
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

const TOKEN_KEY = 'mcl_admin_token'
const USER_KEY = 'mcl_admin_user'
// Measured (device clock − server clock) in ms, captured at login from the
// token's `iat`. All client-side expiry checks correct by this so a device
// with a wrong clock can't mark a freshly-issued token as already expired
// (that caused a login loop on a phone whose clock ran ahead).
const CLOCK_OFFSET_KEY = 'mcl_clock_offset_ms'

// Decode JWT token (simple base64 decode, no verification)
function decodeToken(token: string): {
  userId: number
  email: string
  role: string
  exp: number
  iat?: number
  platform_role?: 'super_admin' | null
  leagues?: Array<{ league_id: number; role: 'admin' | 'editor' | 'stat-keeper' }>
} | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    return null
  }
}

// Stored device→server clock offset (ms). 0 when unknown.
function getClockOffset(): number {
  const v = Number(localStorage.getItem(CLOCK_OFFSET_KEY))
  return Number.isFinite(v) ? v : 0
}

// "Now" corrected into server time, so a skewed device clock doesn't lie about
// expiry. When no offset is known yet this is just Date.now().
function serverNow(): number {
  return Date.now() - getClockOffset()
}

// Check if token is expired — against server-corrected time, with a 60s grace.
function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token)
  if (!decoded || !decoded.exp) return true
  // exp is in seconds; serverNow()/Date.now() are ms.
  return decoded.exp * 1000 < serverNow() - 60_000
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored && !isTokenExpired(stored)) {
      return stored
    }
    if (stored) {
      // Token expired: the session ended (the admin Gate sends the person to
      // /admin/login?next=<page> with the notice). Mark it before clearing.
      markStaffSessionEnded()
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
    }
    return null
  })

  const [user, setUser] = useState<User | null>(() => {
    const stored = localStorage.getItem(USER_KEY)
    if (stored) {
      try {
        return JSON.parse(stored)
      } catch {
        return null
      }
    }
    return null
  })

  const [isLoading, setIsLoading] = useState(true)

  const logout = useCallback(() => {
    // An impersonation session also ends on the server (#623), so Exit really
    // revokes it instead of leaving the token valid until it expires.
    const current = localStorage.getItem(TOKEN_KEY)
    if (current && getImpersonation(current)) void endImpersonation(current)
    setToken(null)
    setUser(null)
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    setIsLoading(false)
  }, [])

  // The token ran out while in use: mark the session ended (so the login page
  // says so and fills the email), then sign out. A deliberate logout() does not.
  const expire = useCallback(() => {
    markStaffSessionEnded()
    logout()
  }, [logout])

  // A 401 ended the session (services/sessionEnd.ts already cleared storage):
  // drop the in-memory session too.
  useEffect(() => onStaffSessionEnded(() => logout()), [logout])

  const login = useCallback((newToken: string, newUser: User) => {
    // Work left by a session that ended is dropped unless the same person is
    // back (services/sessionEnd.ts › staffSignedIn).
    staffSignedIn(newUser.id)
    clearStaffSessionEnd()
    // The server just issued this token, so it's valid by definition. Capture
    // how far this device's clock is from the server (from the token's `iat`)
    // and trust the token unconditionally — never reject a just-issued token on
    // a possibly-wrong local clock (that caused a login loop).
    const decoded = decodeToken(newToken)
    if (decoded?.iat) {
      localStorage.setItem(CLOCK_OFFSET_KEY, String(Date.now() - decoded.iat * 1000))
    }
    setToken(newToken)
    setUser(newUser)
    localStorage.setItem(TOKEN_KEY, newToken)
    localStorage.setItem(USER_KEY, JSON.stringify(newUser))
    setIsLoading(false)
  }, [])

  // Check token validity on mount and periodically
  useEffect(() => {
    // Set loading to false immediately after checking token
    if (token) {
      if (isTokenExpired(token)) {
        // Token expired, clear it
        expire()
      } else {
        // Token is valid, set loading to false
        setIsLoading(false)
        // Check token validity every minute
        const interval = setInterval(() => {
          const currentToken = localStorage.getItem(TOKEN_KEY)
          if (currentToken && isTokenExpired(currentToken)) {
            expire()
          }
        }, 60000) // Check every minute

        return () => clearInterval(interval)
      }
    } else {
      // No token, set loading to false immediately
      setIsLoading(false)
    }
  }, [token, expire])

  const isAuthenticated = token !== null && user !== null && !isTokenExpired(token)

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

